import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import Layout from '@/components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import {
  BookOpen,
  Clock,
  GraduationCap,
  CheckCircle,
  Play,
  FileText,
  MessageSquare,
  Award,
  ArrowLeft,
  Lock,
  Users
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CourseDetail = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [course, setCourse] = useState(null);
  const [modules, setModules] = useState([]);
  const [progress, setProgress] = useState(null);
  const [quizzes, setQuizzes] = useState([]);
  const [forumPosts, setForumPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(false);
  const [isEnrolled, setIsEnrolled] = useState(false);

  useEffect(() => {
    fetchCourseData();
  }, [courseId]);

  const fetchCourseData = async () => {
    try {
      // Fetch course details
      const courseRes = await fetch(`${API}/courses/${courseId}`);
      if (courseRes.ok) {
        const courseData = await courseRes.json();
        setCourse(courseData);
      }

      // Fetch modules
      const modulesRes = await fetch(`${API}/courses/${courseId}/modules`);
      if (modulesRes.ok) {
        const modulesData = await modulesRes.json();
        setModules(modulesData);
      }

      // Fetch quizzes
      const quizzesRes = await fetch(`${API}/quizzes/course/${courseId}`);
      if (quizzesRes.ok) {
        const quizzesData = await quizzesRes.json();
        setQuizzes(quizzesData);
      }

      // Fetch forum posts
      const forumRes = await fetch(`${API}/forums/course/${courseId}`);
      if (forumRes.ok) {
        const forumData = await forumRes.json();
        setForumPosts(forumData);
      }

      // Check enrollment and progress if authenticated
      if (isAuthenticated) {
        try {
          const progressRes = await fetch(`${API}/progress/course/${courseId}`, {
            credentials: 'include'
          });
          if (progressRes.ok) {
            const progressData = await progressRes.json();
            setProgress(progressData);
            setIsEnrolled(true);
          }
        } catch (e) {
          setIsEnrolled(false);
        }
      }
    } catch (error) {
      console.error('Error fetching course data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEnroll = async () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    setEnrolling(true);
    try {
      const res = await fetch(`${API}/courses/${courseId}/enroll`, {
        method: 'POST',
        credentials: 'include'
      });

      if (res.ok) {
        toast.success('Inscription réussie !');
        setIsEnrolled(true);
        fetchCourseData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Erreur lors de l\'inscription');
      }
    } catch (error) {
      toast.error('Erreur lors de l\'inscription');
    } finally {
      setEnrolling(false);
    }
  };

  const handleCompleteModule = async (moduleId) => {
    try {
      const res = await fetch(`${API}/progress/module/${moduleId}/complete`, {
        method: 'POST',
        credentials: 'include'
      });

      if (res.ok) {
        const updatedProgress = await res.json();
        setProgress(updatedProgress);
        toast.success('Module complété !');

        if (updatedProgress.progress_percent >= 100) {
          toast.success('Félicitations ! Vous avez terminé ce cours !', {
            action: {
              label: 'Obtenir mon certificat',
              onClick: () => navigate(`/certificates`)
            }
          });
        }
      }
    } catch (error) {
      toast.error('Erreur lors de la mise à jour de la progression');
    }
  };

  const isModuleCompleted = (moduleId) => {
    return progress?.completed_modules?.includes(moduleId);
  };

  const getLevelLabel = (level) => {
    switch (level) {
      case 'beginner': return 'Débutant';
      case 'intermediate': return 'Intermédiaire';
      case 'advanced': return 'Avancé';
      default: return level;
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-slate-200 rounded w-1/4" />
          <div className="h-12 bg-slate-200 rounded w-3/4" />
          <div className="h-64 bg-slate-200 rounded" />
        </div>
      </Layout>
    );
  }

  if (!course) {
    return (
      <Layout>
        <div className="text-center py-16">
          <BookOpen className="h-16 w-16 text-slate-300 mx-auto mb-4" />
          <h2 className="text-xl font-medium text-slate-900 mb-2">Cours non trouvé</h2>
          <Button onClick={() => navigate('/courses')}>Retour au catalogue</Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8" data-testid="course-detail">
        {/* Back button */}
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4" data-testid="back-btn">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Retour
        </Button>

        {/* Course Header */}
        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            <div>
              <Badge className="mb-3">{course.category}</Badge>
              <h1 className="font-display text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                {course.title}
              </h1>
              <p className="text-lg text-slate-600 leading-relaxed">
                {course.description}
              </p>
            </div>

            <div className="flex flex-wrap gap-6 text-sm text-slate-600">
              <div className="flex items-center gap-2">
                <GraduationCap className="h-5 w-5 text-teal-500" />
                <span>{course.trainer_name}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-teal-500" />
                <span>{course.duration_hours} heures</span>
              </div>
              <div className="flex items-center gap-2">
                <BookOpen className="h-5 w-5 text-teal-500" />
                <span>{modules.length} modules</span>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline">{getLevelLabel(course.level)}</Badge>
              </div>
            </div>

            {course.objectives && course.objectives.length > 0 && (
              <div>
                <h3 className="font-semibold text-slate-900 mb-3">Objectifs du cours</h3>
                <ul className="space-y-2">
                  {course.objectives.map((obj, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <CheckCircle className="h-5 w-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-600">{obj}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <Card className="sticky top-8">
              {course.image_url && (
                <div className="h-48 overflow-hidden rounded-t-lg">
                  <img 
                    src={course.image_url} 
                    alt={course.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              <CardContent className="p-6 space-y-4">
                {isEnrolled ? (
                  <>
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-slate-600">Votre progression</span>
                        <span className="text-sm font-bold text-slate-900">
                          {Math.round(progress?.progress_percent || 0)}%
                        </span>
                      </div>
                      <Progress value={progress?.progress_percent || 0} className="h-3" />
                    </div>
                    <Button 
                      className="w-full bg-slate-900 hover:bg-slate-800"
                      onClick={() => {
                        const firstIncomplete = modules.find(m => !isModuleCompleted(m.module_id));
                        if (firstIncomplete) {
                          document.getElementById(`module-${firstIncomplete.module_id}`)?.scrollIntoView({ behavior: 'smooth' });
                        }
                      }}
                      data-testid="continue-learning-btn"
                    >
                      <Play className="h-4 w-4 mr-2" />
                      Continuer
                    </Button>
                  </>
                ) : (
                  <Button 
                    className="w-full bg-teal-500 hover:bg-teal-600 text-white"
                    onClick={handleEnroll}
                    disabled={enrolling}
                    data-testid="enroll-btn"
                  >
                    {enrolling ? 'Inscription...' : 'S\'inscrire gratuitement'}
                  </Button>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Course Content Tabs */}
        <Tabs defaultValue="modules" className="space-y-6">
          <TabsList>
            <TabsTrigger value="modules" data-testid="modules-tab">
              <BookOpen className="h-4 w-4 mr-2" />
              Modules ({modules.length})
            </TabsTrigger>
            <TabsTrigger value="quizzes" data-testid="quizzes-tab">
              <FileText className="h-4 w-4 mr-2" />
              Quiz ({quizzes.length})
            </TabsTrigger>
            <TabsTrigger value="forum" data-testid="forum-tab">
              <MessageSquare className="h-4 w-4 mr-2" />
              Forum ({forumPosts.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="modules">
            <Card>
              <CardHeader>
                <CardTitle>Contenu du cours</CardTitle>
                <CardDescription>
                  {modules.length} modules - {course.duration_hours} heures au total
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Accordion type="single" collapsible className="space-y-2">
                  {modules.map((module, index) => (
                    <AccordionItem 
                      key={module.module_id} 
                      value={module.module_id}
                      id={`module-${module.module_id}`}
                      className="border rounded-lg px-4"
                    >
                      <AccordionTrigger className="hover:no-underline">
                        <div className="flex items-center gap-4 text-left">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                            isModuleCompleted(module.module_id)
                              ? 'bg-emerald-100 text-emerald-600'
                              : 'bg-slate-100 text-slate-600'
                          }`}>
                            {isModuleCompleted(module.module_id) ? (
                              <CheckCircle className="h-5 w-5" />
                            ) : (
                              <span className="text-sm font-medium">{index + 1}</span>
                            )}
                          </div>
                          <div>
                            <h4 className="font-medium text-slate-900">{module.title}</h4>
                            <p className="text-sm text-slate-500">{module.duration_minutes} min</p>
                          </div>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="pt-4">
                        <p className="text-slate-600 mb-4">{module.description}</p>
                        <div className="prose prose-slate max-w-none mb-4" dangerouslySetInnerHTML={{ __html: module.content.replace(/\n/g, '<br/>').replace(/#{1,3}\s/g, '') }} />
                        {isEnrolled && !isModuleCompleted(module.module_id) && (
                          <Button 
                            onClick={() => handleCompleteModule(module.module_id)}
                            className="mt-4"
                            data-testid={`complete-module-${module.module_id}`}
                          >
                            <CheckCircle className="h-4 w-4 mr-2" />
                            Marquer comme terminé
                          </Button>
                        )}
                        {!isEnrolled && (
                          <div className="flex items-center gap-2 text-slate-500 mt-4">
                            <Lock className="h-4 w-4" />
                            <span className="text-sm">Inscrivez-vous pour accéder au contenu complet</span>
                          </div>
                        )}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="quizzes">
            <Card>
              <CardHeader>
                <CardTitle>Quiz et évaluations</CardTitle>
                <CardDescription>Testez vos connaissances</CardDescription>
              </CardHeader>
              <CardContent>
                {quizzes.length === 0 ? (
                  <div className="text-center py-8">
                    <FileText className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-600">Aucun quiz disponible pour ce cours</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {quizzes.map((quiz) => (
                      <div 
                        key={quiz.quiz_id}
                        className="flex items-center justify-between p-4 border rounded-lg hover:bg-slate-50 transition-colors"
                      >
                        <div>
                          <h4 className="font-medium text-slate-900">{quiz.title}</h4>
                          <p className="text-sm text-slate-500">
                            {quiz.questions?.length || 0} questions - {quiz.time_limit_minutes} min
                          </p>
                        </div>
                        <Button 
                          variant="outline"
                          onClick={() => navigate(`/quiz/${quiz.quiz_id}`)}
                          disabled={!isEnrolled}
                          data-testid={`start-quiz-${quiz.quiz_id}`}
                        >
                          {isEnrolled ? 'Commencer' : 'Inscrivez-vous'}
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="forum">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Forum de discussion</CardTitle>
                  <CardDescription>Échangez avec les autres apprenants</CardDescription>
                </div>
                {isEnrolled && (
                  <Button 
                    onClick={() => navigate(`/forum/${courseId}/new`)}
                    data-testid="new-post-btn"
                  >
                    Nouvelle discussion
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                {forumPosts.length === 0 ? (
                  <div className="text-center py-8">
                    <MessageSquare className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-600">Aucune discussion pour le moment</p>
                    {isEnrolled && (
                      <Button 
                        variant="outline" 
                        className="mt-4"
                        onClick={() => navigate(`/forum/${courseId}/new`)}
                      >
                        Lancer une discussion
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {forumPosts.map((post) => (
                      <div 
                        key={post.post_id}
                        className="p-4 border rounded-lg hover:bg-slate-50 transition-colors cursor-pointer"
                        onClick={() => navigate(`/forum/${courseId}/post/${post.post_id}`)}
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <h4 className="font-medium text-slate-900">{post.title}</h4>
                            <p className="text-sm text-slate-500 mt-1">
                              Par {post.user_name} - {post.replies?.length || 0} réponse(s)
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default CourseDetail;
